import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { SummaryTile } from "./components/SummaryTile";
import { MeetingTypeBarCharts } from "./components/MeetingTypeBarCharts";
import { PieChartSection } from "./components/PieChartSection";
import { useDashboardData, useBusinesses } from "@/services/dashboard";
import { BusinessDropdown } from "./components/BusinessDropdown";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { usePermissions } from "@/hooks/usePermissions";
import { trackPageView } from "@/lib/analytics";
import { useFiscalYears } from "@/services/draftCalendar";
import { GrcDashboard } from "./components/GrcDashboard";


const TILES = [
  {
    key: "totalDemandForDU" as const,
    title: "Total Demand for DU",
    tooltipText: "Total number of room nights requested for the fiscal year."
  },
  {
    key: "totalRoomNightsScheduled" as const,
    title: "Total Room Nights Scheduled",
    tooltipText: "Sum of all room nights scheduled or resolved at DU for the fiscal year."
  },
  {
    key: "totalMeetingsRequested" as const,
    title: "Total # of Meetings Requested",
    tooltipText: "Count of all meetings submitted for scheduling at DU."
  },
  {
    key: "totalMeetingsScheduled" as const,
    title: "Total # of Meetings Scheduled",
    tooltipText: "Count of meetings confirmed or resolved at DU."
  },
  {
    key: "usedPracticalCapcity" as const,
    title: "% Used of Practical Capacity",
    tooltipText: "Percentage of DU’s practical guest room capacity based on 327,600 total room nights available in FY 27."
  }
];

export function DashboardPage() {
  const currentUser = useCurrentUser();
  const { hasPermission } = usePermissions();
  const canViewDashboard = hasPermission("CanViewDashboard");
  const canViewGRCDashboard = hasPermission("CanViewGRCDashboard");

  type DashboardTab = "metrics" | "grc";
  const defaultTab: DashboardTab = canViewDashboard ? "metrics" : "grc";
  const [activeTab, setActiveTab] = useState<DashboardTab>(defaultTab);

  const isProgramOwnerOrProgramReviewer = currentUser?.roles?.roleCode === "business_approver" || currentUser?.roles?.roleCode === "business_reviewer";
  const userId = currentUser?.email ?? "Unknown";

  const { data: fiscalYears = [] } = useFiscalYears();
  const activeFiscalYear = fiscalYears.find((fy) => fy.submissionStatus !== "Closed") ?? fiscalYears[0];

  // Track page view on mount
  useEffect(() => {
    trackPageView("Dashboard", userId);
  }, [userId]);

  const [topBusiness, setTopBusiness] = useState<string | null>(null);
  const [pieRoomBusiness, setPieRoomBusiness] = useState<string | null>(null);
  const [pieDuBusiness, setPieDuBusiness] = useState<string | null>(null);
  const [selectedFyId, setSelectedFyId] = useState<number | null>(null);

  const hasTopFilter = topBusiness !== null;

  const fyId = selectedFyId ?? activeFiscalYear?.id ?? 0;

  const mainQuery    = useDashboardData(hasTopFilter, topBusiness,    fyId);

  // Per-chart drilldown queries — only active when no top filter
  const pieRoomQuery = useDashboardData(false, pieRoomBusiness, fyId);
  const pieDuQuery   = useDashboardData(false, pieDuBusiness,   fyId);

  const { data: businessesMap } = useBusinesses();
  const allBusinesses = businessesMap ? Object.keys(businessesMap) : [];

  const data = mainQuery.data;
  const showInitialLoadingOverlay = mainQuery.isPending && !data;
  const showRefreshLoadingMessage = mainQuery.isFetching && !!data;

  // Room nights pie: top filter → businessLine from main query; per-chart → drilldown query
  const roomNightsData = data?.totalRoomNightsByBusiness ?? [];
  const roomNightsLineData = hasTopFilter
    ? (data?.totalRoomNightsByBusinessLine ?? [])
    : (pieRoomQuery.data?.totalRoomNightsByBusinessLine ?? []);

  // DU percentage pie: same pattern
  const duPercentData = data?.duPercentageRoomNightsByBusiness ?? [];
  const duPercentLineData = hasTopFilter
    ? (data?.duPercentageRoomNightsByBusinessLine ?? [])
    : (pieDuQuery.data?.duPercentageRoomNightsByBusinessLine ?? []);

  const handleTopBusinessChange = (value: string | null) => {
    setTopBusiness(value);
    // Sync pie chart selections when top filter changes
    setPieRoomBusiness(value);
    setPieDuBusiness(value);
  };

  const onFiscalYearChange = (fyCode: string) => {
    const selectedFy = fiscalYears.find((fy) => fy.code === fyCode);
    if (selectedFy) {
      setSelectedFyId(selectedFy.id);
    }
  };
  return (
    <div className="dashboard">
      {(canViewDashboard || canViewGRCDashboard) && (
        <div className="border-b border-slate-200" role="tablist" aria-label="Dashboard sections">
          {canViewDashboard && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "metrics"}
            className={`inline-flex px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === "metrics" ? "border-black bg-black text-white font-semibold" : "border-transparent text-slate-600 hover:text-slate-900"}`}
            onClick={() => setActiveTab("metrics")}
          >
            Draft Calendar Metrics Dashboard
          </button>
          )}
          {canViewGRCDashboard && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "grc"}
            className={`inline-flex px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === "grc" ? "border-black bg-black text-white font-semibold" : "border-transparent text-slate-600 hover:text-slate-900"}`}
            onClick={() => setActiveTab("grc")}
          >
            GRC Summary Dashboard
          </button>
          )}
        </div>
      )}

      {activeTab === "grc" && canViewGRCDashboard ? (
        <GrcDashboard />
      ) : (
        <>
      {showInitialLoadingOverlay && (
        <div className="loading-overlay-react" role="status" aria-live="polite">
          <Loader2
            className="h-10 w-10 animate-spin text-brand"
            aria-label="Loading dashboard"
          />
        </div>
      )}

      {showRefreshLoadingMessage && (
        <div className="dashboard-refresh-overlay" role="status" aria-live="polite">
          <div className="dashboard-refresh-message">
            <Loader2 className="h-5 w-5 animate-spin text-brand" aria-hidden="true" />
            <span>Loading updated dashboard data...</span>
          </div>
        </div>
      )}

      <div className="page-header">
        <div></div>
        <div className="filters">
            <BusinessDropdown
              id="top-business-filter"
              value={topBusiness ?? ""}
              options={[
                { label: "All of DU", value: "" },
                ...allBusinesses.map((b) => ({ label: b, value: b }))
              ]}
              onChange={(v) => handleTopBusinessChange(v || null)}
              aria-label="Filter by business"
            />

            <select
              aria-label="Fiscal year"
              value={fiscalYears.find((fy) => fy.id === fyId)?.code ?? activeFiscalYear?.code ?? ""}
              onChange={(e) => onFiscalYearChange(e.target.value)}
              className="dropdown fiscal-year"
            >
            {isProgramOwnerOrProgramReviewer && <option>{activeFiscalYear?.code ?? "Select Fiscal Year"}</option>}
            {!isProgramOwnerOrProgramReviewer && fiscalYears.map((fy) => (
              <option key={fy.code} value={fy.code}>
                {fy.code}
              </option>
            ))}
            </select>
        </div>
      </div>

      <div className="main-content">
        {mainQuery.isError && (
          <p role="alert" className="dashboard-error-alert">
            Failed to load dashboard data. Please try refreshing the page.
          </p>
        )}

        {data && (
          <>
            <section aria-label="Summary metrics" className="stats-row">
              {TILES.map((tile) => (
                <SummaryTile
                  key={tile.key}
                  title={tile.title}
                  value={data[tile.key] ?? "—"}
                  tooltipText={tile.tooltipText}
                />
              ))}
            </section>

            <section aria-label="Programs by meeting type" className="charts-row">
              <MeetingTypeBarCharts data={data.totalProgramByMeetingType ?? []} />
            </section>

            <section aria-label="Room nights by business" className="charts-row">
              <PieChartSection
                title="Total # of Room Nights by Business"
                tooltipText="Total number of room nights booked by each business or business line."
                data={roomNightsData}
                businessLineData={roomNightsLineData}
                allBusinesses={allBusinesses}
                otherHoverItems={data.otherBusinessTotalRoomNightHover}
                disabled={hasTopFilter}
                selectedBusiness={hasTopFilter ? topBusiness : pieRoomBusiness}
                onBusinessChange={setPieRoomBusiness}
              />
              <PieChartSection
                title="% of DU Room Nights by Business / Line"
                tooltipText="Percentage share of total DU room nights by business or business line."
                data={duPercentData}
                businessLineData={duPercentLineData}
                allBusinesses={allBusinesses}
                otherHoverItems={data.otherBusinessDUPercentageRoomNightsHover}
                disabled={hasTopFilter}
                selectedBusiness={hasTopFilter ? topBusiness : pieDuBusiness}
                onBusinessChange={setPieDuBusiness}
              />
            </section>

            {data.lastUpdated && (
              <div className="last-updated">
                <p>
                  <strong>Last updated:</strong> {data.lastUpdated}
                </p>
              </div>
            )}
          </>
        )}
      </div>
        </>
      )}
    </div>
  );
}

